export interface SubscriptionDto {
  _id?: string;
  id?: string;
  name: string;
  userId: string;
  topic: string;
  tagFilter?: Record<string, unknown>;
  defaultChannels?: string[];
  channelOverrides?: Record<string, Record<string, unknown>>;
  paused?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Subscription {
  id: string;
  name: string;
  userId: string;
  topic: string;
  tagFilter: Record<string, unknown>;
  defaultChannels: string[];
  channelOverrides: Record<string, Record<string, unknown>>;
  paused: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export function fromDtoToSubscription(dto: SubscriptionDto): Subscription {
  return {
    id: dto.id ?? dto._id ?? '',
    name: dto.name,
    userId: dto.userId,
    topic: dto.topic,
    tagFilter: dto.tagFilter ?? {},
    defaultChannels: dto.defaultChannels ?? [],
    channelOverrides: dto.channelOverrides ?? {},
    paused: dto.paused ?? false,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export interface CreateSubscriptionRequest {
  name: string;
  topic: string;
  tagFilter?: Record<string, unknown>;
  defaultChannels?: string[];
  channelOverrides?: Record<string, Record<string, unknown>>;
}

// topic is immutable after creation
export interface UpdateSubscriptionRequest {
  name?: string;
  tagFilter?: Record<string, unknown>;
  defaultChannels?: string[];
  channelOverrides?: Record<string, Record<string, unknown>>;
  paused?: boolean;
}

export interface TagAllowedNode {
  key: string;
  name?: string; // UI label; key is the canonical value
  children?: (string | TagAllowedNode)[]; // absent ⇒ leaf
}

export type TagAllowedEntry = string | TagAllowedNode;

export interface TagSchemaField {
  type?: string;
  required?: boolean;
  name?: string;
  description?: string;
  allowed?: TagAllowedEntry[];
}

export interface Topic {
  key: string;
  description: string;
  displayName?: string;
  tagSchema: Record<string, TagSchemaField>;
  applicationId?: string;
}

export interface ChannelConfigField {
  key: string;
  name: string;
  type: string;
  required: boolean;
  description?: string;
  isIdentity?: boolean;
}

export interface NotificationChannel {
  key: string;
  displayName: string;
  instructions: string | null;
  configFields: ChannelConfigField[];
  // Absent on a not-yet-deployed backend — callers should treat `undefined`
  // the same as `true` (assume verification is required) so a stale backend
  // degrades to "no inline add, link to Contacts instead" rather than risking
  // a confusing 422 by letting a channel that actually needs verification
  // through the inline-add / immediate-override path.
  requiresVerification?: boolean;
  // Whether a contact on this channel can carry a cosmetic `label` (e.g.
  // "Team alerts" instead of a raw webhook URL). Absent (not-yet-deployed
  // backend) is treated as `false` — the API 422s a label sent for a channel
  // that doesn't support it, so the label input must stay hidden by default
  // rather than risk that failure.
  supportsLabel?: boolean;
}

export function identityFieldKey(channel: NotificationChannel): string | undefined {
  return channel.configFields.find(field => field.isIdentity)?.key;
}

export function configDisplayValue(
  channel: NotificationChannel,
  config: Record<string, unknown>,
): string {
  const key = identityFieldKey(channel);
  const val = key ? config[key] : Object.values(config)[0];
  return val != null ? String(val) : '';
}

export interface SubscriptionFilter {
  mode: 'allowlist' | 'denylist';
  sourceIds: string[];
}

export function isRestrictedByFilter(filter: SubscriptionFilter | null, sourceId: string): boolean {
  if (!filter) return false;
  const included = filter.sourceIds.includes(sourceId);
  return filter.mode === 'allowlist' ? !included : included;
}

export function deviceLabelFor(contact: Contact): string | undefined {
  const label = contact.config.deviceLabel;
  return typeof label === 'string' && label ? label : undefined;
}

export interface ContactDto {
  _id?: string;
  id?: string;
  userId: string;
  channelKey: string;
  identityValue: string;
  config: Record<string, unknown>;
  isDefault?: boolean;
  isVerified?: boolean;
  subscriptionFilter?: SubscriptionFilter | null;
  createdAt?: string;
  // Cosmetic only — absent/null when never set. See Contact.label.
  label?: string | null;
}

export interface Contact {
  id: string;
  userId: string;
  channelKey: string;
  identityValue: string;
  config: Record<string, unknown>;
  isDefault: boolean;
  isVerified: boolean;
  subscriptionFilter: SubscriptionFilter | null;
  createdAt?: string;
  // A human-friendly name (e.g. "Team alerts" for a Slack webhook), only on
  // channels where NotificationChannel.supportsLabel is true. Purely
  // cosmetic — never sent as part of a subscription's channelOverrides and
  // never affects delivery. Undefined when never set (or on a channel that
  // doesn't support it).
  label?: string;
}

export function fromDtoToContact(dto: ContactDto): Contact {
  return {
    id: dto.id ?? dto._id ?? '',
    userId: dto.userId,
    channelKey: dto.channelKey,
    identityValue: dto.identityValue,
    config: dto.config,
    isDefault: dto.isDefault ?? false,
    isVerified: dto.isVerified ?? false,
    subscriptionFilter: dto.subscriptionFilter ?? null,
    createdAt: dto.createdAt,
    label: dto.label ?? undefined,
  };
}

// The primary text to show for a contact: its friendly label when it has
// one, falling back to the raw identityValue (e.g. a webhook URL) — the
// thing that made channels like Slack hard to tell apart before labels.
export function contactDisplayLabel(contact: Contact): string {
  return contact.label?.trim() ? contact.label : contact.identityValue;
}

export interface NotificationsProfile {
  userId: string;
  displayName?: string;
  /** message-gateway STOMP topic for real-time in-app notifications — subscribe to it as-is. */
  inAppTopic: string;
}

export interface BroadcastPreference {
  paused?: boolean;
  channelOverrides?: Record<string, Record<string, unknown>>;
}

export interface ReceivedBroadcastDto {
  _id?: string;
  id?: string;
  name: string;
  createdBy: string;
  topic: string;
  roleIds?: string[];
  defaultChannels?: string[];
  preference?: BroadcastPreference;
}

export interface ReceivedBroadcast {
  id: string;
  name: string;
  createdBy: string;
  topic: string;
  roleIds: string[];
  defaultChannels: string[];
  preference?: BroadcastPreference;
}

export function fromDtoToReceivedBroadcast(dto: ReceivedBroadcastDto): ReceivedBroadcast {
  return {
    id: dto.id ?? dto._id ?? '',
    name: dto.name,
    createdBy: dto.createdBy,
    topic: dto.topic,
    roleIds: dto.roleIds ?? [],
    defaultChannels: dto.defaultChannels ?? [],
    preference: dto.preference,
  };
}

export type NotificationActionType = 'url' | 'deeplink' | 'custom';

export interface NotificationAction {
  label: string;
  actionType: NotificationActionType;
  value: string;
}

export interface UserNotification {
  id: string;
  readAt: string | null;
  createdAt: string;
  source: { type: string; id: string };
  notification: {
    id: string;
    topicKey: string;
    occurredAt: string;
    tags: Record<string, unknown>;
    payload: {
      title: string;
      body: string;
      value?: string;
      eventDate?: string;
      dataSeriesId?: number;
      [key: string]: unknown;
    };
    actions?: NotificationAction[];
  };
}

export interface NotificationPage {
  items: UserNotification[];
  nextCursor?: string;
  total?: number;
}

export const UNREAD_COUNT_CAP = 100;

export const formatUnreadCount = (count: number) =>
  count >= UNREAD_COUNT_CAP ? '99+' : String(count);

export interface NotificationApplication {
  id: string;
  name: string;
}

export interface GetNotificationsParams {
  cursor?: string;
  limit?: number;
  status?: 'read' | 'unread';
  publisher?: string;
  subscription?: string;
}

export interface ContactVerificationPreview {
  channelKey: string;
  channelDisplayName: string;
  identityValue: string;
  expiresAt: string;
}
